"use client";

import { useMemo } from "react";
import { Label } from "@/shadcn-components/ui/label";
import { Input } from "@/shadcn-components/ui/input";
import { Switch } from "@/shadcn-components/ui/switch";
import IndicatorParameters from "../indicators/common/IndicatorParameters";
import type { ParsedIndicatorInput } from "@/lib/custom-indicators/types";
import { parsedInputsToCatalogParameters } from "@/lib/custom-indicators/indicator-source";

export function defaultValuesFromParsedInputs(
  parsed: ParsedIndicatorInput[]
): Record<string, number | string> {
  const params = parsedInputsToCatalogParameters(parsed);
  const initial: Record<string, number | string> = {};
  params.forEach((p) => {
    initial[p.name] = p.defaultValue;
  });
  for (const input of parsed) {
    if (input.type === "bool") {
      initial[input.name] =
        input.defaultValue === true ? "true" : "false";
    } else if (input.type === "color" && initial[input.name] == null) {
      initial[input.name] =
        typeof input.defaultValue === "string"
          ? input.defaultValue
          : "#2962FF";
    } else if (input.type === "string" && initial[input.name] == null) {
      initial[input.name] =
        typeof input.defaultValue === "string"
          ? input.defaultValue
          : String(input.defaultValue ?? "");
    }
  }
  return initial;
}

export function mergeInputValuesWithParsed(
  prev: Record<string, number | string>,
  parsed: ParsedIndicatorInput[]
): Record<string, number | string> {
  const defaults = defaultValuesFromParsedInputs(parsed);
  const next: Record<string, number | string> = {};
  for (const input of parsed) {
    next[input.name] =
      prev[input.name] !== undefined ? prev[input.name] : defaults[input.name];
  }
  return next;
}

interface CustomIndicatorInputsFormProps {
  parsedInputs: ParsedIndicatorInput[];
  values: Record<string, number | string>;
  onValueChange: (name: string, value: number | string) => void;
  readOnly?: boolean;
}

export default function CustomIndicatorInputsForm({
  parsedInputs,
  values,
  onValueChange,
  readOnly = false,
}: CustomIndicatorInputsFormProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, ParsedIndicatorInput[]>();
    for (const input of parsedInputs) {
      const g = input.group ?? "Parameters";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(input);
    }
    return [...map.entries()];
  }, [parsedInputs]);

  if (grouped.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6 rounded-lg border border-dashed border-border">
        No [Input] fields declared in this script
      </p>
    );
  }

  return (
    <fieldset
      disabled={readOnly}
      className="space-y-4 border-0 p-0 m-0 min-w-0 disabled:opacity-100"
    >
      {grouped.map(([groupName, groupInputs]) => {
        const catalogInputs = groupInputs.filter(
          (i) =>
            i.type === "number" ||
            (i.type === "select" && i.options?.length)
        );
        const otherInputs = groupInputs.filter(
          (i) => !catalogInputs.includes(i)
        );
        return (
          <section key={groupName}>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              {groupName}
            </h2>
            <div className="rounded-lg border border-border px-3">
              {catalogInputs.length > 0 ? (
                <IndicatorParameters
                  parameters={parsedInputsToCatalogParameters(catalogInputs)}
                  values={values}
                  onValueChange={onValueChange}
                />
              ) : null}
              {otherInputs.map((input) => (
                <IndicatorInputField
                  key={input.name}
                  input={input}
                  value={values[input.name]}
                  readOnly={readOnly}
                  onChange={(v) => onValueChange(input.name, v)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </fieldset>
  );
}

function IndicatorInputField({
  input,
  value,
  onChange,
  readOnly = false,
}: {
  input: ParsedIndicatorInput;
  value: number | string | undefined;
  onChange: (v: number | string) => void;
  readOnly?: boolean;
}) {
  if (input.type === "bool") {
    const checked =
      value === "true" ||
      (value == null && input.defaultValue === true);
    return (
      <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
        <Label className="text-sm font-normal">{input.label}</Label>
        <Switch
          checked={checked}
          disabled={readOnly}
          onCheckedChange={(c) => onChange(c ? "true" : "false")}
        />
      </div>
    );
  }

  if (input.type === "color") {
    const color =
      typeof value === "string"
        ? value
        : typeof input.defaultValue === "string"
          ? input.defaultValue
          : "#2962FF";
    return (
      <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border/60 last:border-0">
        <Label className="text-sm font-normal">{input.label}</Label>
        <Input
          type="color"
          value={color.startsWith("#") ? color : "#2962FF"}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-14 p-0.5 cursor-pointer"
        />
      </div>
    );
  }

  const text =
    value != null
      ? String(value)
      : typeof input.defaultValue === "string"
        ? input.defaultValue
        : "";
  return (
    <div className="flex flex-col gap-1.5 py-2.5 border-b border-border/60 last:border-0">
      <Label className="text-sm font-normal">{input.label}</Label>
      <Input
        value={text}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 text-sm"
      />
    </div>
  );
}
